import React, { useEffect } from 'react';
import { cn } from '~/lib/utils';

interface YakSplashScreenProps {
  isExiting?: boolean;
  onTransitionOut?: () => void;
}

export function YakSplashScreen({
  isExiting = false,
  onTransitionOut,
}: YakSplashScreenProps) {
  useEffect(() => {
    if (isExiting && onTransitionOut) {
      const timer = setTimeout(onTransitionOut, 400);
      return () => clearTimeout(timer);
    }
  }, [isExiting, onTransitionOut]);

  const containerStyle: React.CSSProperties = {
    opacity: isExiting ? 0 : 1,
    transition: isExiting ? 'opacity 400ms ease-out' : 'opacity 300ms ease-in',
  };

  const logoStyle: React.CSSProperties = {
    animation: 'pulse-breathe 2s ease-in-out infinite',
  };

  const textStyle: React.CSSProperties = {
    animation: 'fade-in 600ms ease-in forwards',
    opacity: 0,
  };

  const dotsStyle: React.CSSProperties = {
    animation: 'fade-in 800ms ease-in forwards',
    opacity: 0,
  };

  return (
    <>
      <style>{`
        @keyframes pulse-breathe {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }

        @keyframes fade-in {
          to {
            opacity: 1;
          }
        }

        @keyframes dot-pulse {
          0%, 20% {
            opacity: 0.3;
          }
          50% {
            opacity: 1;
          }
          80%, 100% {
            opacity: 0.3;
          }
        }

        .dot-pulse-1 {
          animation: dot-pulse 1.4s infinite;
        }

        .dot-pulse-2 {
          animation: dot-pulse 1.4s infinite 0.2s;
        }

        .dot-pulse-3 {
          animation: dot-pulse 1.4s infinite 0.4s;
        }
      `}</style>
      <div
        role="status"
        aria-label="Loading"
        className={cn(
          'min-h-screen w-full flex flex-col items-center justify-center',
          'bg-gradient-to-b from-[#1a1a2e] to-[#16213e]'
        )}
        style={containerStyle}
      >
        <div className="flex flex-col items-center gap-8">
          {/* Logo */}
          <div className="w-32 h-32 flex items-center justify-center" style={logoStyle}>
            <img
              src="/assets/images/yak_logo_small.svg"
              alt="Yak Interactive"
              className="w-full h-full object-contain"
            />
          </div>

          {/* Brand Name */}
          <h1
            className="text-3xl font-bold text-white tracking-wide"
            style={textStyle}
          >
            Yak Interactive
          </h1>

          {/* Loading Dots */}
          <div
            className="flex items-center gap-2"
            style={dotsStyle}
          >
            <div className="w-2 h-2 rounded-full bg-[#6366f1] dot-pulse-1" />
            <div className="w-2 h-2 rounded-full bg-[#8b5cf6] dot-pulse-2" />
            <div className="w-2 h-2 rounded-full bg-[#6366f1] dot-pulse-3" />
          </div>
        </div>
      </div>
    </>
  );
}

YakSplashScreen.displayName = 'YakSplashScreen';
